import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, ShieldCheck, AlertCircle, HelpCircle, CheckCircle2 } from "lucide-react";
import {
  setup3ds,
  deviceDataComplete,
  enrollment3ds,
  challengeStart,
  validate3ds,
  status3ds,
  origenConfiableExterno,
  SIMULACION_3DS,
} from "@/data/auth3dsStore";
import { authorizeF2A, statusF2A, finalizeF2A, validarBillTo } from "@/data/pagosF2aStore";
import { DireccionBancoCampos } from "@/screens/app/DireccionBancoCampos";
import DeviceDataCollection from "@/screens/app/DeviceDataCollection";
import StepUpChallenge from "@/screens/app/StepUpChallenge";

/**
 * Orquestador Payer Authentication 3DS + Autorización/Captura · Sprint 59 · F1B+F2A.
 *
 * 3DS (Setup → DDC → Enrollment → Challenge → Validate) y, al autenticar, F2A:
 * captura de billTo → solicita el pago UNA sola vez → muestra estados distintos
 * (procesando / aprobado-finalizado / rechazado / no confirmado). Navega a los
 * boletos SÓLO tras FINALIZED. Completar 3DS NO significa pago aprobado.
 * Tokens, billTo y material 3DS jamás se guardan en URL/localStorage.
 */

const MENSAJES = {
  DEVICE_DATA_PENDING: "Verificando tu dispositivo de forma segura…",
  DEVICE_DATA_COMPLETED: "Consultando la inscripción 3D Secure…",
  ENROLLMENT_PROCESSING: "Consultando la inscripción 3D Secure…",
  CHALLENGE_REQUIRED: "Tu banco solicita una verificación adicional.",
  CHALLENGE_IN_PROGRESS: "Esperando a que completes la verificación…",
  CHALLENGE_RETURNED: "Validando el resultado de la autenticación…",
};

const BILLTO_INICIAL = { address1: "", locality: "", administrativeArea: "", postalCode: "", country: "MX" };

export const Pago3DSFlow = ({ compraId, transientJwt, onFinalized }) => {
  const [sesion, setSesion] = useState(null);
  const [fase, setFase] = useState("iniciando"); // iniciando|activo|ok|rechazado|error|expirado
  const [mensaje, setMensaje] = useState("");
  const iniciadoRef = useRef(false);
  const enrollLanzadoRef = useRef(false);

  // ── F2A · pago ──────────────────────────────────────────────────────
  const [pagoFase, setPagoFase] = useState("billto"); // billto|procesando|finalizado|rechazado|desconocido|error
  const [billTo, setBillTo] = useState(BILLTO_INICIAL);
  const [erroresBillTo, setErroresBillTo] = useState({});
  const [pagoMsg, setPagoMsg] = useState("");
  const pagoInFlightRef = useRef(false);
  // Reanudación de APPROVED (idempotente, sin reenviar el pago): una sola vez.
  const pagoResumeRef = useRef(false);

  const aplicar = useCallback((data) => {
    if (!data || data.error) {
      setFase("error");
      setMensaje("No pudimos iniciar la autenticación segura. Intenta de nuevo.");
      return null;
    }
    setSesion(data);
    const e = data.estado;
    if (e === "FRICTIONLESS_AUTHENTICATED" || e === "AUTHENTICATION_VALIDATED") {
      setFase("ok");
    } else if (e === "AUTHENTICATION_REJECTED") {
      setFase("rechazado");
      setMensaje(data.mensaje || "No pudimos autenticar tu tarjeta. Intenta con otra.");
    } else if (e === "EXPIRED") {
      setFase("expirado");
      setMensaje("Tu reserva o la autenticación expiraron. Vuelve a iniciar el pago.");
    } else if (e === "ERROR") {
      setFase("error");
      setMensaje(data.mensaje || "La autenticación no está disponible por ahora.");
    } else {
      setFase("activo");
    }
    return data;
  }, []);

  useEffect(() => {
    if (iniciadoRef.current || !compraId || !transientJwt) return;
    iniciadoRef.current = true;
    (async () => {
      const previo = await status3ds(compraId);
      if (previo && !previo.error && previo.estado) {
        aplicar(previo);
        return;
      }
      aplicar(await setup3ds(compraId, transientJwt));
    })();
  }, [compraId, transientJwt, aplicar]);

  const onDdcListo = useCallback(async () => {
    if (enrollLanzadoRef.current) return;
    enrollLanzadoRef.current = true;
    const sid = sesion?.sessionId;
    if (!sid) return;
    await deviceDataComplete(sid);
    aplicar(await enrollment3ds(sid));
  }, [sesion, aplicar]);

  const onChallengeIframeReady = useCallback(async () => {
    const sid = sesion?.sessionId;
    if (sid && sesion?.estado === "CHALLENGE_REQUIRED") {
      const r = await challengeStart(sid);
      if (r && !r.error) {
        setSesion((prev) => ({ ...prev, estado: r.estado || "CHALLENGE_IN_PROGRESS" }));
        setFase("activo");
      }
    }
  }, [sesion]);

  const onChallengeReturned = useCallback(async () => {
    const sid = sesion?.sessionId;
    const nonce = sesion?.challenge?.nonce;
    if (!sid || !nonce) {
      setFase("error");
      setMensaje("No pudimos validar la verificación. Intenta de nuevo.");
      return;
    }
    aplicar(await validate3ds(sid, nonce));
  }, [sesion, aplicar]);

  useEffect(() => {
    if (sesion?.estado === "CHALLENGE_REQUIRED") onChallengeIframeReady();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesion?.estado]);

  // ── F2A · aplica el estado del intento de pago ──────────────────────
  const aplicarPago = useCallback(
    (data) => {
      pagoInFlightRef.current = false;
      const e = data?.estado;
      if (e === "FINALIZED") {
        setPagoFase("finalizado");
        setTimeout(() => onFinalized && onFinalized(compraId), 900);
      } else if (e === "DECLINED") {
        setPagoFase("rechazado");
        setPagoMsg("Tu pago fue rechazado por el banco. Puedes intentar con otra tarjeta.");
      } else if (e === "UNKNOWN") {
        setPagoFase("desconocido");
        setPagoMsg("No pudimos confirmar el resultado del pago. NO intentes pagar de nuevo: revisaremos el estado y, si se cobró, aparecerá en Mis Boletos.");
      } else if (e === "ERROR_RETRYABLE") {
        setPagoFase("error");
        setPagoMsg("Ocurrió un error antes de procesar el pago. Puedes intentarlo de nuevo.");
      } else if (data?.error || e === "ERROR_FINAL") {
        setPagoFase("error");
        const det = typeof data?.error === "object" ? data.error.detail : "";
        setPagoMsg(det || "No pudimos procesar el pago. Intenta más tarde.");
      } else if (e === "APPROVED") {
        // APPROVED sin FINALIZED (caída entre aprobación y emisión): reanuda la
        // finalización LOCAL, idempotente y SIN reenviar el pago. Una sola vez.
        setPagoFase("procesando");
        if (!pagoResumeRef.current) {
          pagoResumeRef.current = true;
          finalizeF2A(compraId).then((st) => {
            if (st?.estado === "FINALIZED") {
              setPagoFase("finalizado");
              setTimeout(() => onFinalized && onFinalized(compraId), 900);
            } else {
              pagoResumeRef.current = false;
              setPagoFase("procesando");
            }
          });
        }
      } else {
        setPagoFase("procesando");
      }
    },
    [compraId, onFinalized],
  );

  // Recuperación tras F5 cuando ya se autenticó 3DS: no reinicia el pago.
  useEffect(() => {
    if (fase !== "ok") return;
    (async () => {
      const st = await statusF2A(compraId);
      if (st && !st.error && st.estado && st.estado !== "SIN_INTENTO") {
        aplicarPago(st);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase, compraId]);

  const pagar = useCallback(async () => {
    if (pagoInFlightRef.current) return; // bloqueo doble clic / dos pestañas
    const errs = validarBillTo(billTo);
    setErroresBillTo(errs);
    if (Object.keys(errs).length) return;
    pagoInFlightRef.current = true;
    setPagoFase("procesando");
    const payload = {
      address1: billTo.address1.trim(),
      locality: billTo.locality.trim(),
      administrativeArea: billTo.administrativeArea.trim().toUpperCase(),
      postalCode: billTo.postalCode.trim(),
      country: billTo.country.trim().toUpperCase(),
    };
    aplicarPago(await authorizeF2A(compraId, sesion?.sessionId, payload));
  }, [billTo, compraId, sesion, aplicarPago]);


  // ─── Render 3DS terminal negativo ─────────────────────────────────────
  if (fase === "rechazado" || fase === "error" || fase === "expirado") {
    return (
      <section data-testid={`tds-${fase}`} role="alert" className="rounded-2xl border hairline bg-destructive/5 dark:bg-destructive/10 p-6 shadow-soft flex items-start gap-3">
        <span className="w-9 h-9 grid place-items-center rounded-xl bg-destructive/10 text-destructive shrink-0">
          <AlertCircle className="w-4 h-4" strokeWidth={1.7} />
        </span>
        <p className="text-sm text-foreground">{mensaje}</p>
      </section>
    );
  }

  // ─── Render 3DS autenticado → F2A (pago) ─────────────────────────────
  if (fase === "ok") {
    // La política de pago la decide el backend (campo `pagable`). Si NO es
    // pagable, no anunciamos autenticación exitosa ni ofrecemos Pagar.
    if (sesion?.pagable === false) {
      const motivo = sesion?.motivoNoPagable;
      const texto = motivo === "ATTEMPT_BLOQUEADO"
        ? "Tu banco registró un intento de autenticación 3D Secure, no una verificación completa. Para proteger el pago, este comercio no permite continuar con este resultado. Intenta con otra tarjeta."
        : motivo === "AUTENTICACION_NO_APTA"
          ? "La autenticación 3D Secure no quedó en un estado válido para el pago. Intenta de nuevo o usa otra tarjeta."
          : motivo === "MATERIAL_INSUFICIENTE"
            ? "La autenticación 3D Secure no entregó los datos necesarios para procesar el pago de forma segura con esta tarjeta. Intenta con otra tarjeta."
            : motivo === "RED_DESCONOCIDA"
              ? "No pudimos confirmar la red de tu tarjeta para procesar el pago de forma segura. Intenta con otra tarjeta."
              : "No es posible continuar con el pago con el resultado de autenticación actual. Intenta con otra tarjeta.";
      return (
        <section data-testid="tds-no-pagable" role="alert" className="rounded-2xl border hairline bg-amber-500/5 dark:bg-amber-500/10 p-6 shadow-soft flex items-start gap-3">
          <span className="w-9 h-9 grid place-items-center rounded-xl bg-amber-500/15 text-amber-700 dark:text-amber-300 shrink-0">
            <AlertCircle className="w-4 h-4" strokeWidth={1.7} />
          </span>
          <p className="text-sm text-foreground">{texto}</p>
        </section>
      );
    }
    if (pagoFase === "finalizado") {
      return (
        <section data-testid="tds-pago-finalizado" className="rounded-2xl border hairline bg-card p-6 shadow-soft flex items-start gap-3">
          <span className="w-9 h-9 grid place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.7} />
          </span>
          <p className="text-sm text-foreground">¡Compra confirmada! Estamos preparando tus boletos…</p>
        </section>
      );
    }
    if (pagoFase === "procesando") {
      return (
        <section data-testid="tds-pago-procesando" className="rounded-2xl border hairline bg-card p-6 shadow-soft flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-accent" />
          <p className="text-sm text-muted-foreground">Procesando pago… no cierres esta ventana.</p>
        </section>
      );
    }
    if (pagoFase === "desconocido") {
      return (
        <section data-testid="tds-pago-desconocido" role="alert" className="rounded-2xl border hairline bg-amber-500/5 dark:bg-amber-500/10 p-6 shadow-soft flex items-start gap-3">
          <span className="w-9 h-9 grid place-items-center rounded-xl bg-amber-500/15 text-amber-700 dark:text-amber-300 shrink-0">
            <HelpCircle className="w-4 h-4" strokeWidth={1.7} />
          </span>
          <p className="text-sm text-foreground">{pagoMsg}</p>
        </section>
      );
    }
    if (pagoFase === "rechazado" || pagoFase === "error") {
      return (
        <section data-testid={`tds-pago-${pagoFase}`} role="alert" className="rounded-2xl border hairline bg-destructive/5 dark:bg-destructive/10 p-6 shadow-soft flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 grid place-items-center rounded-xl bg-destructive/10 text-destructive shrink-0">
              <AlertCircle className="w-4 h-4" strokeWidth={1.7} />
            </span>
            <p className="text-sm text-foreground">{pagoMsg}</p>
          </div>
          {pagoFase === "error" && (
            <button type="button" data-testid="tds-pago-reintentar" onClick={() => { setPagoFase("billto"); setPagoMsg(""); }}
              className="self-start rounded-full px-5 py-2.5 text-sm font-semibold bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground">
              Volver a intentar
            </button>
          )}
        </section>
      );
    }
    // pagoFase === "billto"
    return (
      <section data-testid="tds-billto-form" className="rounded-2xl border hairline bg-card p-6 shadow-soft flex flex-col gap-5">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 grid place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
            <ShieldCheck className="w-4 h-4" strokeWidth={1.7} />
          </span>
          <div>
            <p className="text-sm text-foreground font-medium" data-testid="tds-3ds-titulo">
              {sesion?.resultadoTipo === "ATTEMPT"
                ? "Autenticación 3D Secure registrada (intento)."
                : "Autenticación 3D Secure completada."}
            </p>
            <p className="text-xs text-muted-foreground">Aún no se ha realizado ningún cargo.</p>
          </div>
        </div>
        <div>
          <h3 data-testid="tds-billto-titulo" className="text-sm font-medium text-foreground">Dirección registrada en tu banco</h3>
          <p data-testid="tds-billto-ayuda" className="text-xs text-muted-foreground mt-1">Corresponde a la tarjeta que usarás para tu compra.</p>
        </div>
        <DireccionBancoCampos valores={billTo} errores={erroresBillTo} onCambio={setBillTo} />
        <button
          type="button"
          data-testid="tds-btn-pagar"
          onClick={pagar}
          disabled={pagoInFlightRef.current}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-wide bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft transition-all active:scale-[0.99] hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          Pagar de forma segura
        </button>
      </section>
    );
  }

  // ─── Render 3DS en curso ──────────────────────────────────────────────
  return (
    <section data-testid="tds-flow" className="flex flex-col gap-4">
      <div className="rounded-2xl border hairline bg-card p-5 shadow-soft flex items-center gap-3">
        <Loader2 className="w-4 h-4 animate-spin text-accent" />
        <span data-testid="tds-estado" className="text-sm text-muted-foreground">
          {MENSAJES[sesion?.estado] || "Iniciando autenticación segura…"}
        </span>
      </div>

      {sesion?.estado === "DEVICE_DATA_PENDING" && sesion?.deviceDataCollection && (
        (() => {
          const expectedOrigin = origenConfiableExterno(sesion.deviceDataCollection.url);
          if (!expectedOrigin) {
            return (
              <p data-testid="tds-ddc-origen-invalido" className="text-sm text-destructive">
                URL de verificación no confiable. Intenta de nuevo.
              </p>
            );
          }
          return (
            <DeviceDataCollection
              url={sesion.deviceDataCollection.url}
              accessToken={sesion.deviceDataCollection.accessToken}
              expectedOrigin={expectedOrigin}
              timeoutMs={SIMULACION_3DS ? 2500 : 10000}
              onComplete={onDdcListo}
              onTimeout={onDdcListo}
            />
          );
        })()
      )}

      {sesion?.estado === "CHALLENGE_IN_PROGRESS" && sesion?.challenge && (
        <StepUpChallenge
          stepUpUrl={sesion.challenge.stepUpUrl}
          accessToken={sesion.challenge.accessToken}
          challengeWindowSize={sesion.challenge.challengeWindowSize}
          onReturned={onChallengeReturned}
          onTimeout={() => {
            setFase("error");
            setMensaje("La verificación tardó demasiado. Intenta de nuevo.");
          }}
        />
      )}
    </section>
  );
};

export default Pago3DSFlow;
